import type { NavigationGuardWithThis, RouteLocation, RouteLocationRaw } from 'vue-router';

import type { UserRole } from '@/models/user-role';

import type { RouteName } from '@/router/routes';

import { seatAllocationStore } from '@/store/seat-allocation';
import { userStore } from '@/store/user';

const accessDeniedRoute: RouteLocationRaw = { name: 'AccessDenied' };

interface AccessRule {
  allow: boolean;
  routes: RouteName[];
}

function isAccessDenied(to: RouteLocation, allow: boolean, ...routes: RouteName[]): boolean {
  return !allow && routes.includes(to.name as RouteName);
}

function isPublicRoute(to: RouteLocation): boolean {
  return (['Login', 'AccessDenied'] as RouteName[]).includes(to.name as RouteName);
}

/**
 * Determines route redirects for users without allocated seats.
 * Prioritizing billing and user management routes if accessible, otherwise redirecting to a no seats available page.
 */
function getNoSeatRedirect(
  to: RouteLocation,
  role: UserRole,
  seatAllocated: boolean,
  availableSeats: number,
): RouteLocationRaw | undefined {
  if (seatAllocated || availableSeats >= 0) {
    return undefined;
  }

  const isAllowedBillingRoute = role.canEditBilling && to.name === 'Billing';
  const isAllowedUserRoute = role.canEditUsers && to.name === 'UserManagement';

  if (isAllowedBillingRoute || isAllowedUserRoute) {
    return undefined;
  }

  if (role.canEditBilling && to.name !== 'Billing') {
    return { name: 'Billing' };
  }

  if (role.canEditUsers && to.name !== 'UserManagement') {
    return { name: 'UserManagement' };
  }

  if (to.name !== 'NoSeatsAvailable') {
    return { name: 'NoSeatsAvailable' };
  }

  return undefined;
}

/**
 * Determines if the route being accessed is restricted based on the user's role permissions.
 */
function isRoleRestrictedRoute(to: RouteLocation, role: UserRole): boolean {
  const accessRules: AccessRule[] = [
    { allow: role.canSeeDashboard, routes: ['Dashboard'] },
    { allow: role.canSeeTargets, routes: ['TargetManagement', 'TargetDetails'] },
    { allow: role.canSeeRules, routes: ['RuleManagement', 'RuleDetails'] },
    { allow: role.canSeeChannels, routes: ['ChannelManagement', 'ChannelDetails'] },
    {
      allow: role.canSeeUsers,
      routes: ['UserManagement', 'UserDetails', 'SkillManagement', 'TeamManagement', 'TeamDetails'],
    },
    { allow: role.canSeeCatalog, routes: ['CatalogManagement'] },
    { allow: role.canSeeTariffs, routes: ['TariffManagement', 'TariffDetails'] },
    { allow: role.canSeeTariffs, routes: ['TariffSandbox'] },
    { allow: role.canEditSettings, routes: ['Settings'] },
    { allow: role.canSeeUserRoles, routes: ['UserRoleManagement'] },
    { allow: role.canSeeBilling, routes: ['Billing'] },
    { allow: role.canSeeSsoConfig, routes: ['SSOConfig'] },
    // The Entwicklertools views read and act on tenant-wide infrastructure - the Valkey
    // keyspace, feature flags, IO recalculation and raw customer data.
    // Hiding the menu entry does not restrict them: accessControl only drops a nav item, and
    // the route is reachable by typing the path. This list is what actually denies it, and
    // the routes must appear here or they are never denied at all.
    {
      allow: role.canEditSettings,
      routes: [
        'DevInteractionOpportunities',
        'DevCustomersOverview',
        'DevRecalcTrigger',
        'DevFeatureFlags',
        'DevValkey',
      ],
    },
  ];

  return accessRules.some(rule => isAccessDenied(to, rule.allow, ...rule.routes));
}

/**
 * Global navigation guard to enforce authentication and role-based access control across the application routes.
 */
const authGuard: NavigationGuardWithThis<void> = (to: RouteLocation) => {
  if (!userStore.loggedIn && !isPublicRoute(to)) {
    return {
      name: 'Login',
      query: {
        redirect: to.fullPath,
        dev: to.query.dev, // required so it does not get lost after the login redirect
      },
    };
  }

  if (userStore.loggedIn) {
    const { role, seatAllocated } = userStore.user;

    const noSeatRedirect = getNoSeatRedirect(to, role, seatAllocated, seatAllocationStore.availableSeats);
    if (noSeatRedirect) {
      return noSeatRedirect;
    }

    if (isRoleRestrictedRoute(to, role)) {
      return accessDeniedRoute;
    }

    if (isAccessDenied(to, role.canSeeAuditLogs, 'AuditLogs')) {
      return accessDeniedRoute;
    }
  }

  return true;
};

export default authGuard;
